import { useEffect, useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import {
  useGuardarModulos, useModulosDeUsuario, useRestablecerModulos,
  type ModuloDeUsuario, type Usuario,
} from './api';
import { NOMBRES_DE_MODULO } from './piezas';

/**
 * ModulosDeUsuario — las excepciones individuales sobre lo que da el rol.
 *
 * Lo que decide qué ve cada persona es su **rol**. Aquí se abren o se cierran
 * módulos sueltos para una persona concreta, y el servidor guarda eso como una
 * excepción: por eso cada fila dice si viene del rol o es una excepción.
 *
 * «Restablecer al rol» borra las excepciones. Es destructivo en el sentido que
 * importa —se pierde lo que alguien decidió a mano— así que pregunta, y la
 * pregunta se apila sobre el panel en vez de sustituirlo.
 */
export function ModulosDeUsuario({
  usuario, onCerrar,
}: {
  usuario: Usuario;
  onCerrar: () => void;
}) {
  const consulta = useModulosDeUsuario(usuario.id);
  const guardar = useGuardarModulos();
  const restablecer = useRestablecerModulos();

  const [estado, setEstado] = useState<Record<string, boolean>>({});
  const [restableciendo, setRestableciendo] = useState(false);

  useEffect(() => {
    if (!consulta.data) return;
    setEstado(Object.fromEntries(consulta.data.map((m) => [m.module_key, m.is_enabled])));
  }, [consulta.data]);

  const modulos: ModuloDeUsuario[] = consulta.data ?? [];

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v && !guardar.isPending) onCerrar(); }}
      titulo="Módulos"
      descripcion={`Qué ve ${usuario.full_name} en el portal. Lo normal es que lo decida su rol.`}
      ancho="md"
      pie={
        <>
          <Button
            variant="ghost"
            className="mr-auto"
            onClick={() => setRestableciendo(true)}
          >
            Restablecer al rol
          </Button>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button
            loading={guardar.isPending}
            onClick={() => guardar.mutate(
              {
                id: usuario.id,
                modulos: modulos.map((m) => ({
                  module_key: m.module_key,
                  is_enabled: !!estado[m.module_key],
                })),
              },
              {
                onSuccess: () => { Utils.toast('Módulos actualizados', 'success'); onCerrar(); },
                onError: (err) => Utils.toast(
                  err instanceof Error ? err.message : 'No se pudo guardar', 'error'),
              },
            )}
          >
            Guardar
          </Button>
        </>
      }
    >
      {consulta.isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="los módulos del usuario"
          onReintentar={() => void consulta.refetch()}
        />
      ) : (
        <ul className="flex flex-col gap-1.5">
          {modulos.map((m) => (
            <li
              key={m.module_key}
              className="flex items-center gap-3 rounded-sm border border-line px-3 py-2"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-3 font-semibold text-ink">
                  {NOMBRES_DE_MODULO[m.module_key] ?? m.module_key}
                </span>
                {m.state === 'override' && (
                  <Badge tone="warn">excepción individual</Badge>
                )}
              </span>
              <label className="flex cursor-pointer items-center gap-2 text-2 text-ink-2">
                <input
                  type="checkbox"
                  checked={!!estado[m.module_key]}
                  onChange={(e) => setEstado((s) => ({ ...s, [m.module_key]: e.target.checked }))}
                />
                <span className="sr-only">
                  {NOMBRES_DE_MODULO[m.module_key] ?? m.module_key}
                </span>
                {estado[m.module_key] ? 'Lo ve' : 'No lo ve'}
              </label>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        abierto={restableciendo}
        onCambio={setRestableciendo}
        titulo="Restablecer al rol"
        descripcion={`Se borran las excepciones individuales de ${usuario.full_name}. Quedará exactamente con los módulos de su rol «${usuario.role}».`}
        textoConfirmar="Restablecer"
        cargando={restablecer.isPending}
        onConfirmar={() => restablecer.mutate(usuario.id, {
          onSuccess: (r) => {
            Utils.toast(r.message || 'Restablecido al rol', 'success');
            setRestableciendo(false);
            onCerrar();
          },
          onError: (err) => {
            setRestableciendo(false);
            Utils.toast(err instanceof Error ? err.message : 'No se pudo', 'error');
          },
        })}
      />
    </Drawer>
  );
}
